"use client";
import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Element } from "react-scroll";

/* ─────────────────────────────────────────
   ANIMATION VARIANTS
───────────────────────────────────────── */
const fadeUp = {
  hidden: { y: 48, opacity: 0 },
  show: (d = 0) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.75, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.05 } },
};

const cardVariant = {
  hidden: { y: 60, opacity: 0, scale: 0.97 },
  show: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

/* ─────────────────────────────────────────
   EDUCATION CARD
───────────────────────────────────────── */
function EducationCard({ education, index }) {
  const ref = useRef(null);
  const isLeft = index % 2 === 0;

  return (
    <motion.div
      ref={ref}
      variants={cardVariant}
      className="relative flex flex-col md:flex-row items-stretch gap-0 group"
    >
      {/* ── Timeline node (center) ── */}
      <div className="hidden md:flex absolute left-1/2 top-8 -translate-x-1/2 z-20 flex-col items-center">
        {/* Outer ring */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.5,
            delay: index * 0.15 + 0.3,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="relative w-10 h-10 rounded-full flex items-center justify-center"
          style={{
            background: "linear-gradient(135deg, #0047ff, #00f0ff)",
            boxShadow: "0 0 20px rgba(0,240,255,0.35)",
          }}
        >
          <div
            className="w-4 h-4 rounded-full"
            style={{ background: "#05050b" }}
          />
          {/* Pulse ring */}
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{ border: "1px solid rgba(0,240,255,0.5)" }}
            animate={{ scale: [1, 1.6, 1], opacity: [0.6, 0, 0.6] }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 0.4,
            }}
          />
        </motion.div>
      </div>

      {/* ── Left half ── */}
      <div
        className={`md:w-1/2 md:pr-12 ${isLeft ? "md:text-right" : "md:invisible"}`}
      >
        {isLeft && (
          <CardContent education={education} index={index} align="right" />
        )}
      </div>

      {/* ── Right half ── */}
      <div className={`md:w-1/2 md:pl-12 ${!isLeft ? "" : "md:invisible"}`}>
        {!isLeft && (
          <CardContent education={education} index={index} align="left" />
        )}
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   CARD CONTENT
───────────────────────────────────────── */
function CardContent({ education, index, align }) {
  const isRight = align === "right";

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className={`relative mb-12 p-6 rounded-2xl border border-white/[0.06]
        bg-white/[0.025] backdrop-blur-sm overflow-hidden
        hover:border-[#00f0ff]/25 hover:bg-[#00f0ff]/[0.03]
        transition-colors duration-400 group cursor-default
        ${isRight ? "ml-auto" : ""}`}
    >
      {/* Inner glow on hover */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: isRight
            ? "linear-gradient(225deg, rgba(0,240,255,0.05) 0%, transparent 60%)"
            : "linear-gradient(45deg, rgba(0,240,255,0.05) 0%, transparent 60%)",
        }}
      />

      {/* Corner bracket */}
      {isRight ? (
        <svg
          className="absolute top-3 right-3 opacity-20 group-hover:opacity-50 transition-opacity duration-300"
          width="16"
          height="16"
          fill="none"
        >
          <path d="M16 0 L16 16 L0 16" stroke="#00f0ff" strokeWidth="1.2" />
        </svg>
      ) : (
        <svg
          className="absolute top-3 left-3 opacity-20 group-hover:opacity-50 transition-opacity duration-300"
          width="16"
          height="16"
          fill="none"
        >
          <path d="M0 0 L0 16 L16 16" stroke="#00f0ff" strokeWidth="1.2" />
        </svg>
      )}

      {/* Year badge */}
      <div className={`flex mb-3 ${isRight ? "justify-end" : "justify-start"}`}>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full
            border border-[#0047ff]/30 bg-[#0047ff]/[0.07]
            text-[10px] tracking-[0.22em] text-[#00f0ff]/70 uppercase"
          style={{ fontFamily: "'DM Mono', monospace" }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff]/60 flex-shrink-0" />
          {education?.start_date
            ? `${new Date(education.start_date).getFullYear()} — ${
                education?.end_date
                  ? new Date(education.end_date).getFullYear()
                  : "Present"
              }`
            : education?.year || "Present"}
        </span>
      </div>

      {/* Degree */}
      <h3
        className={`text-xl md:text-2xl font-bold text-white leading-tight mb-2 ${isRight ? "text-right" : "text-left"}`}
        style={{ fontFamily: "'Syne', sans-serif" }}
      >
        {education?.degree || education?.field_of_study || "Degree"}
      </h3>

      {/* Institution */}
      <p
        className={`text-sm font-medium text-[#00f0ff]/70 mb-3 tracking-wide ${isRight ? "text-right" : "text-left"}`}
        style={{ fontFamily: "'DM Mono', monospace" }}
      >
        {education?.institution || education?.school || "Institution"}
      </p>

      {/* Divider */}
      <div
        className={`h-px w-12 mb-3 ${isRight ? "ml-auto" : ""}`}
        style={{
          background: "linear-gradient(90deg, #0047ff, #00f0ff60, transparent)",
        }}
      />

      {/* Description */}
      {education?.description && (
        <p
          className={`text-sm text-white/40 leading-relaxed ${isRight ? "text-right" : "text-left"}`}
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {education.description}
        </p>
      )}

      {/* Download link */}
      {education?.certificate_link && (
        <div
          className={`mt-4 flex ${isRight ? "justify-end" : "justify-start"}`}
        >
          <motion.a
            href={education.certificate_link}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase
              text-[#00f0ff]/60 border border-[#00f0ff]/20 px-3 py-1.5 rounded-full
              hover:border-[#00f0ff]/50 hover:text-[#00f0ff] hover:bg-[#00f0ff]/5
              transition-all duration-300"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            Certificate ↗
          </motion.a>
        </div>
      )}
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   TIMELINE LINE (vertical)
───────────────────────────────────────── */
function TimelineLine({ count }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <div
      ref={ref}
      className="hidden md:block absolute left-1/2 -translate-x-1/2 top-12 z-10 pointer-events-none"
      style={{ height: `calc(100% - 3rem)` }}
    >
      <motion.div
        initial={{ scaleY: 0 }}
        animate={inView ? { scaleY: 1 } : {}}
        transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-px h-full origin-top"
        style={{
          background:
            "linear-gradient(to bottom, #0047ff80, #00f0ff40, #0047ff20, transparent)",
        }}
      />
    </div>
  );
}

/* ─────────────────────────────────────────
   MOBILE TIMELINE LINE
───────────────────────────────────────── */
function MobileTimelineLine() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <div
      ref={ref}
      className="md:hidden absolute left-[7px] top-0 bottom-0 z-0 pointer-events-none"
    >
      <motion.div
        initial={{ scaleY: 0 }}
        animate={inView ? { scaleY: 1 } : {}}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="w-px h-full origin-top"
        style={{
          background:
            "linear-gradient(to bottom, #0047ff60, #00f0ff30, transparent)",
        }}
      />
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN EDUCATION COMPONENT
───────────────────────────────────────── */
const Education = ({ educationData }) => {
  const headRef = useRef(null);
  const listRef = useRef(null);
  const headInView = useInView(headRef, { once: true, margin: "-60px" });
  const listInView = useInView(listRef, { once: true, margin: "-80px" });

  const data = educationData?.data ?? educationData ?? [];
  if (!data?.length) return null;

  const formattedEducation = data.map((item) => ({
    _id: item?._id,
    start_date: item.startTime,
    end_date: item.isPresent ? null : item.endTime,
    degree: item.degree,
    field_of_study: item.field,
    institution: item.institute_name,
    description: item.short_description,
  }));

  return (
    <Element name="education">
      <section
        className="relative overflow-hidden"
        style={{ background: "#05050b" }}
      >
        {/* Grid bg */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.03]"
          aria-hidden
        >
          <defs>
            <pattern
              id="edu-grid"
              width="72"
              height="72"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 72 0 L 0 0 0 72"
                fill="none"
                stroke="white"
                strokeWidth="0.4"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#edu-grid)" />
        </svg>

        {/* Accent orbs */}
        <div
          className="absolute top-0 left-0 w-[400px] h-[400px] pointer-events-none"
          aria-hidden
        >
          <div
            style={{
              background:
                "radial-gradient(circle, rgba(0,71,255,0.1) 0%, transparent 70%)",
              filter: "blur(80px)",
              width: "100%",
              height: "100%",
            }}
          />
        </div>
        <div
          className="absolute bottom-0 right-0 w-[350px] h-[350px] pointer-events-none"
          aria-hidden
        >
          <div
            style={{
              background:
                "radial-gradient(circle, rgba(0,240,255,0.07) 0%, transparent 70%)",
              filter: "blur(80px)",
              width: "100%",
              height: "100%",
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 py-10 ">
          {/* ── HEADING ── */}
          <div ref={headRef} className="mb-20 lg:mb-24">
            {/* Label */}
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate={headInView ? "show" : "hidden"}
              className="flex items-center gap-4 mb-5"
            >
              <div className="h-px w-12 bg-gradient-to-r from-[#0047ff] to-[#00f0ff]" />
              <span
                className="text-[10px] tracking-[0.32em] text-[#00f0ff]/60 uppercase"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                03 — Education
              </span>
            </motion.div>

            {/* Big heading */}
            <div className="overflow-hidden">
              <motion.h2
                custom={0.1}
                variants={fadeUp}
                initial="hidden"
                animate={headInView ? "show" : "hidden"}
                className="text-[clamp(3rem,9vw,7.5rem)] font-black leading-[0.88] tracking-tight text-white"
                style={{ fontFamily: "'Syne', sans-serif" }}
              >
                ACADEMIC
              </motion.h2>
            </div>
            <div className="overflow-hidden">
              <motion.h2
                custom={0.2}
                variants={fadeUp}
                initial="hidden"
                animate={headInView ? "show" : "hidden"}
                className="text-[clamp(3rem,9vw,7.5rem)] font-black leading-[0.88] tracking-tight"
                style={{
                  fontFamily: "'Syne', sans-serif",
                  WebkitTextStroke: "1.5px rgba(0,240,255,0.32)",
                  color: "transparent",
                }}
              >
                JOURNEY.
              </motion.h2>
            </div>

            {/* Rule */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={headInView ? { scaleX: 1 } : {}}
              transition={{
                duration: 1,
                delay: 0.35,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="mt-6 h-px w-40 origin-left"
              style={{
                background:
                  "linear-gradient(90deg, #0047ff, #00f0ff60, transparent)",
              }}
            />

            {/* Subtitle */}
            <motion.p
              custom={0.38}
              variants={fadeUp}
              initial="hidden"
              animate={headInView ? "show" : "hidden"}
              className="mt-5 text-sm text-white/30 max-w-sm leading-relaxed"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              The foundation that shaped an engineer who thinks in systems.
            </motion.p>
          </div>

          {/* ── TIMELINE ── */}
          <div ref={listRef} className="relative">
            {/* Desktop center line */}
            <TimelineLine count={data.length} />

            {/* Mobile left line */}
            <MobileTimelineLine />

            {/* Cards */}
            <motion.div
              variants={stagger}
              initial="hidden"
              animate={listInView ? "show" : "hidden"}
              className="relative space-y-2"
            >
              {formattedEducation?.map((edu, i) => (
                <EducationCard key={edu?._id ?? i} education={edu} index={i} />
              ))}
            </motion.div>
          </div>

          {/* ── Bottom count ── */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={listInView ? { opacity: 1 } : {}}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-16 flex items-center gap-4"
          >
            <div
              className="h-px flex-1 max-w-7xl"
              style={{
                background:
                  "linear-gradient(90deg, transparent, #0047ff30, transparent)",
              }}
            />
            <span
              className="text-[9px] tracking-[0.28em] text-white/18 uppercase"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              {data.length} qualification{data.length !== 1 ? "s" : ""}
            </span>
            <div
              className="h-px flex-1 max-w-7xl"
              style={{
                background:
                  "linear-gradient(90deg, transparent, #00f0ff20, transparent)",
              }}
            />
          </motion.div>
        </div>
      </section>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Mono:ital,wght@0,300;0,400;0,500&family=DM+Sans:wght@300;400;500&display=swap');
      `}</style>
    </Element>
  );
};

export default Education;
